import { useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getCycleDateRange } from '@/utils/cycle-dates';
import type { Member } from '@shared/api.interface';
import { getMemberUsedBorrowedQuota } from '@shared/member-quota';
import {
  ChevronRight,
  History,
  LogOut,
  MessageCircle,
  ShieldCheck,
} from 'lucide-react';

interface MemberBenefitsDialogProps {
  session: {
    passcode: string;
    member: Member;
    remainingCycle: number;
    remainingToday: number;
  };
  triggerClassName?: string;
  /** 受控打开：手机端点顶部额度胶囊时使用 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 不渲染自带的触发按钮 */
  hideTrigger?: boolean;
  /** 传入后面板底部显示对应入口 */
  onOpenHistory?: () => void;
  onContact?: () => void;
  contactLabel?: string;
  onLogout?: () => void;
}

function getStatusText(status: Member['status']): string {
  if (status === 'active') return '正常生效';
  if (status === 'expired') return '已过期';
  return '已冻结';
}

export function MemberBenefitsDialog({
  session,
  triggerClassName,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
  onOpenHistory,
  onContact,
  contactLabel = '续费 / 联系客服',
  onLogout,
}: MemberBenefitsDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const { member } = session;
  const isTotalMode = member.quota_mode === 'total';
  const isActive = member.status === 'active';
  const showDailyLimit = member.daily_limit > 0;
  const totalQuota = member.total_quota ?? 0;
  const borrowed = isTotalMode ? 0 : member.next_cycle_borrowed ?? 0;
  const borrowedUsed = isTotalMode ? 0 : getMemberUsedBorrowedQuota(member);
  const repaid = isTotalMode ? 0 : member.cycle_deduction ?? 0;
  // 进度条按“还剩多少”画：本周期可用总数 = 月配额 + 预支转入 - 偿还上期
  const quotaLimit = isTotalMode
    ? totalQuota
    : member.monthly_quota + (member.cycle_bonus ?? 0) - (member.cycle_deduction ?? 0);

  const cycleDates = useMemo(
    () => (!isTotalMode ? getCycleDateRange(member.cycle_start_date) : null),
    [isTotalMode, member.cycle_start_date]
  );

  const runAfterClose = (action?: () => void) => {
    if (!action) return;
    setOpen(false);
    action();
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          variant="outline"
          size="sm"
          className={triggerClassName}
          onClick={() => setOpen(true)}
        >
          <ShieldCheck className="mr-1 h-4 w-4" />
          我的权益
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] max-w-md gap-0 overflow-hidden bg-background p-0">
          <DialogHeader className="px-4 pb-2 pt-5 text-left sm:px-6">
            <DialogTitle className="text-xl">我的权益</DialogTitle>
            <DialogDescription className="sr-only">
              查看剩余可领次数、有效期和领取记录
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[calc(90dvh-64px)]">
            <div className="space-y-3 px-4 pb-5 sm:px-6">
              {/* 权益卡：白底 + 主题橙，先看还能领几次 */}
              <div className="space-y-3.5 rounded-2xl bg-white p-4 shadow-[0_0_0_1px_#F0DCC4] sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-foreground">{member.member_type}</span>
                    <span
                      className={`inline-flex h-[22px] items-center rounded-full px-2 text-xs font-semibold ${
                        isActive ? 'bg-claimed-soft text-claimed' : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {getStatusText(member.status)}
                    </span>
                  </div>
                  {member.expiry_date && (
                    <span className="text-[13px] text-muted-foreground">{member.expiry_date} 到期</span>
                  )}
                </div>

                <div className="flex items-end gap-1.5">
                  <span className="text-[46px] font-extrabold leading-[0.9] text-cta">{session.remainingCycle}</span>
                  <span className="pb-0.5 text-[15px] text-muted-foreground">
                    次 · {isTotalMode ? '还可领取' : '本周期还可领'}
                  </span>
                </div>

                {quotaLimit > 0 && (
                  <div className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${Math.min(100, Math.round((session.remainingCycle / quotaLimit) * 100))}%` }}
                    />
                  </div>
                )}

                <div className="grid grid-cols-3 text-center">
                  {isTotalMode ? (
                    <>
                      <QuotaStat value={String(totalQuota)} label="总次数" />
                      <QuotaStat value={String(member.total_redemptions)} label="已领取" divided />
                      <QuotaStat value={member.last_redemption_date || '暂无'} label="最后领取" />
                    </>
                  ) : (
                    <>
                      <QuotaStat value={String(session.remainingToday)} label="今日可领" />
                      <QuotaStat value={String(member.cycle_used)} label="本周期已领" divided />
                      <QuotaStat value={cycleDates?.nextResetDate || '-'} label="次数恢复" />
                    </>
                  )}
                </div>

                {(borrowed > 0 || repaid > 0) && (
                  <div className="space-y-0.5 rounded-xl bg-orange-50/70 px-3 py-2 text-[13px] leading-relaxed text-amber-900">
                    {borrowed > 0 &&
                      (borrowedUsed > 0 ? (
                        <p>已预支下周期 {borrowed} 次（已用 {borrowedUsed} 次），下周期只扣用掉的 {borrowedUsed} 次。</p>
                      ) : (
                        <p>已预支下周期 {borrowed} 次，目前还没用到；没用完的会自动退回，不占下周期额度。</p>
                      ))}
                    {repaid > 0 && <p>本周期已扣还上期预支 {repaid} 次。</p>}
                  </div>
                )}
              </div>

              <div className="rounded-2xl bg-card px-4 shadow-[0_0_0_1px_var(--border)]">
                <InfoRow label="兑换码" value={session.passcode} mono />
                <InfoRow label="开通时间" value={member.start_date || '-'} />
                <InfoRow label="最后领取" value={member.last_redemption_date || '暂无'} />
              </div>

              {(onOpenHistory || onContact || onLogout) && (
                <div className="overflow-hidden rounded-2xl bg-card shadow-[0_0_0_1px_var(--border)]">
                  {onOpenHistory && (
                    <ActionRow icon={<History className="h-5 w-5 text-cta" />} label="领取记录" onClick={() => runAfterClose(onOpenHistory)} />
                  )}
                  {onContact && (
                    <ActionRow icon={<MessageCircle className="h-5 w-5 text-cta" />} label={contactLabel} onClick={() => runAfterClose(onContact)} />
                  )}
                  {onLogout && (
                    <ActionRow
                      icon={<LogOut className="h-5 w-5" />}
                      label="退出登录"
                      muted
                      onClick={() => runAfterClose(onLogout)}
                    />
                  )}
                </div>
              )}

              <p className="px-1 text-xs leading-relaxed text-muted-foreground">
                {isTotalMode
                  ? `有效期内共可领取 ${totalQuota} 次，不按周期恢复。`
                  : `每 30 天恢复一次可领取次数，${showDailyLimit ? `单日最多领取 ${member.daily_limit} 次` : '单日不限'}。已领取的主题可在领取记录中随时找回。`}
              </p>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InfoRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 py-3 text-sm last:border-b-0">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className={`min-w-0 break-all text-right font-medium text-foreground ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function ActionRow({
  icon,
  label,
  onClick,
  muted = false,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-14 w-full items-center gap-3 border-b border-border/60 px-4 text-left text-[15px] font-medium transition-colors last:border-b-0 hover:bg-accent ${
        muted ? 'text-muted-foreground' : 'text-foreground'
      }`}
    >
      {icon}
      <span className="flex-1">{label}</span>
      {!muted && <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />}
    </button>
  );
}

function QuotaStat({ value, label, divided = false }: { value: string; label: string; divided?: boolean }) {
  return (
    <div className={divided ? 'border-x border-border/60' : undefined}>
      <div className="text-[17px] font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
